import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ModelPlatform } from '../types/prompt';
import { useAuth } from './AuthContext';
import { subscriptionService } from '../services/subscription';
import { SYSTEM_API_KEYS, shouldUseSystemKeys } from '../config/systemApiKeys';

export interface ApiKeys {
  openai?: string;
  anthropic?: string;
  google?: string;
  stability?: string;
  replicate?: string;
  mistral?: string;
  meta?: string;
  cohere?: string;
}

export interface ModelApiMapping {
  'gpt4': 'openai';
  'gpt4-vision': 'openai';
  'dalle3': 'openai';
  'claude': 'anthropic';
  'gemini': 'google';
  'google-imagefx': 'google';
  'veo': 'google';
  'stable-diffusion-xl': 'stability';
  'midjourney': 'replicate';
  'llama': 'meta';
  'mistral': 'mistral';
}

interface ApiKeyContextType {
  apiKeys: ApiKeys;
  setApiKey: (provider: keyof ApiKeys, key: string) => void;
  getApiKeyForModel: (model: ModelPlatform) => string | undefined;
  validateApiKey: (provider: keyof ApiKeys, key: string) => Promise<boolean>;
  isKeyConfigured: (model: ModelPlatform) => boolean;
  clearApiKey: (provider: keyof ApiKeys) => void;
  clearAllApiKeys: () => void;
  testConnection: (provider: keyof ApiKeys) => Promise<{ success: boolean; message: string }>;
  requiresOwnKeys: boolean;
}

const ApiKeyContext = createContext<ApiKeyContextType | undefined>(undefined);

const modelApiMapping: Record<string, keyof ApiKeys> = {
  'gpt4': 'openai',
  'gpt4-vision': 'openai',
  'dalle3': 'openai',
  'claude': 'anthropic',
  'gemini': 'google',
  'google-imagefx': 'google',
  'veo': 'google',
  'stable-diffusion-xl': 'stability',
  'midjourney': 'replicate',
  'llama': 'meta',
  'mistral': 'mistral',
  'universal': 'openai', // Default to OpenAI for universal
};

export const ApiKeyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { profile, isAuthenticated } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKeys>(() => {
    // Load from localStorage on init (fallback for non-authenticated users)
    const stored = localStorage.getItem('api_keys');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse stored API keys:', e);
        return {};
      }
    }
    return {};
  });

  // Sync with Firebase profile API keys when authenticated
  useEffect(() => {
    if (isAuthenticated && profile?.apiKeys) {
      const newKeys: ApiKeys = {};
      
      // Map Firebase keys to our ApiKeys interface
      if (profile.apiKeys.openai) newKeys.openai = profile.apiKeys.openai;
      if (profile.apiKeys.anthropic) newKeys.anthropic = profile.apiKeys.anthropic;
      // Handle both "gemini" and "google" keys from Firebase
      if (profile.apiKeys.gemini) newKeys.google = profile.apiKeys.gemini;
      if (profile.apiKeys.google) newKeys.google = profile.apiKeys.google;
      if (profile.apiKeys.stability) newKeys.stability = profile.apiKeys.stability;
      if (profile.apiKeys.replicate) newKeys.replicate = profile.apiKeys.replicate;
      if (profile.apiKeys.mistral) newKeys.mistral = profile.apiKeys.mistral;
      if (profile.apiKeys.meta) newKeys.meta = profile.apiKeys.meta;
      if (profile.apiKeys.cohere) newKeys.cohere = profile.apiKeys.cohere;
      
      // Always update keys when profile changes
      setApiKeys(newKeys);
    }
  }, [profile, isAuthenticated]);

  // Save to localStorage whenever apiKeys change (for non-authenticated users)
  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem('api_keys', JSON.stringify(apiKeys));
    }
  }, [apiKeys, isAuthenticated]);

  const setApiKey = (provider: keyof ApiKeys, key: string) => {
    setApiKeys(prev => ({
      ...prev,
      [provider]: key
    }));
  };

  const getApiKeyForModel = (model: ModelPlatform): string | undefined => {
    const provider = modelApiMapping[model];
    if (!provider) return undefined;
    
    // Check if user is Pro/Premium and should use system keys
    const userTier = profile?.subscription || 'free';
    if (shouldUseSystemKeys(userTier)) {
      // Map provider to system key from environment
      switch (provider) {
        case 'openai':
          return process.env.REACT_APP_OPENAI_API_KEY || SYSTEM_API_KEYS.openai;
        case 'anthropic':
          return process.env.REACT_APP_ANTHROPIC_API_KEY || SYSTEM_API_KEYS.anthropic;
        case 'google':
          return process.env.REACT_APP_GOOGLE_AI_API_KEY || SYSTEM_API_KEYS.googleAI;
        default:
          // For other providers, still use system OpenAI key as fallback
          return process.env.REACT_APP_OPENAI_API_KEY || SYSTEM_API_KEYS.openai;
      }
    }
    
    // Free users need their own keys
    return apiKeys[provider];
  };

  const isKeyConfigured = (model: ModelPlatform): boolean => {
    // Check if user is Pro/Premium
    const userTier = profile?.subscription || 'free';
    if (shouldUseSystemKeys(userTier)) {
      // Pro users always have keys configured
      return true;
    }
    
    // Free users need to check their own keys
    const key = getApiKeyForModel(model);
    return !!key && key.length > 0;
  };

  const clearApiKey = (provider: keyof ApiKeys) => {
    setApiKeys(prev => {
      const newKeys = { ...prev };
      delete newKeys[provider];
      return newKeys;
    });
  };

  const clearAllApiKeys = () => {
    setApiKeys({});
    localStorage.removeItem('api_keys');
  };

  const validateApiKey = async (provider: keyof ApiKeys, key: string): Promise<boolean> => {
    if (!key) return false;

    try {
      switch (provider) {
        case 'openai':
          // Test OpenAI API key
          const openaiResponse = await fetch('https://api.openai.com/v1/models', {
            headers: {
              'Authorization': `Bearer ${key}`
            }
          });
          return openaiResponse.ok;

        case 'anthropic':
          // Anthropic doesn't have a simple validation endpoint
          // We'll assume it's valid if it matches the pattern
          return key.startsWith('sk-ant-');

        case 'google':
          // Google API key validation would require a test request
          // For now, basic validation
          return key.length > 20;

        case 'stability':
          // Stability AI key validation
          return key.startsWith('sk-');

        default:
          // Basic validation for other providers
          return key.length > 10;
      }
    } catch (error) {
      console.error(`Error validating ${provider} API key:`, error);
      return false;
    }
  };

  const testConnection = async (provider: keyof ApiKeys): Promise<{ success: boolean; message: string }> => {
    const key = apiKeys[provider];
    if (!key) {
      return { success: false, message: 'No API key configured' };
    }

    try {
      const isValid = await validateApiKey(provider, key);
      if (isValid) {
        return { success: true, message: 'Connection successful!' };
      } else {
        return { success: false, message: 'Invalid API key or connection failed' };
      }
    } catch (error) {
      return { success: false, message: `Connection error: ${error}` };
    }
  };

  const userTier = profile?.subscription || 'free';
  const requiresOwnKeys = !shouldUseSystemKeys(userTier);
  
  const value: ApiKeyContextType = {
    apiKeys,
    setApiKey,
    getApiKeyForModel,
    validateApiKey,
    isKeyConfigured,
    clearApiKey,
    clearAllApiKeys,
    testConnection,
    requiresOwnKeys
  };

  return (
    <ApiKeyContext.Provider value={value}>
      {children}
    </ApiKeyContext.Provider>
  );
};

export const useApiKeys = () => {
  const context = useContext(ApiKeyContext);
  if (context === undefined) {
    throw new Error('useApiKeys must be used within an ApiKeyProvider');
  }
  return context;
};